import React, { useState, useEffect, useRef } from 'react';
import Grid from 'components/elements/Grid';
import FieldSetWithTitle from 'shared/FieldSetWithTitle/components/FieldSetWithTitle';
import useStores from 'utils/useStores';
import { observer } from 'mobx-react';
import {
  OpportunityCheckbox,
  OpportunitySelectGroup,
  OpportunityTextInput,
} from '../OpportunityFields';
import { customFetch } from 'utils/fetch';
import Routes from 'routes';
import {
  BASE_CURRENCY,
  BASE_UNIT_OF_MEASURE,
  isBaseCurrency,
  isBaseUnitOfMeasure,
  needsPriceConversion,
  usdLbToCustomerPrice,
  customerPriceToUsdLb,
} from '../../utils/priceConversion';

const OpportunityContract = ({
  contractTypeField,
  contractType,
  setContractType,
  priceTypeField,
  priceTypeOptions,
  priceType,
  setPriceType,
  probabilityField,
  fixedPriceField,
  differentialField,
  priceUsdLbField,
  modifiedExchangeRateField,
  opportunityPricesUsdToCustomerRateField,
  incoterm,
  incotermField,
  setIncoterm,
  sampleNeededField,
  sampleNeeded,
  setSampleNeeded,
}) => {
  const { opportunityStore } = useStores();
  const [modifiedExchangeRate, setModifiedExchangeRate] = useState(
    modifiedExchangeRateField?.value || false
  );
  const [fixedPrice, setFixedPrice] = useState(fixedPriceField?.value);
  const [priceUsdLb, setPriceUsdLb] = useState(priceUsdLbField?.value || fixedPrice);
  const [opportunityPricesUsdToCustomerRate, setOpportunityPricesUsdToCustomerRate] =
    useState(opportunityPricesUsdToCustomerRateField?.value || 1);
  const [sameCurrency, setSameCurrency] = useState(
    isBaseCurrency(opportunityStore.selectedCustomer?.currency) || false
  );

  const [sameUnitOfMeasure, setSameUnitOfMeasure] = useState(
    isBaseUnitOfMeasure(opportunityStore.selectedCustomer?.unit_of_measure) || false
  );

  const getMeasurementRate = () => {
    if (fixedPrice && opportunityPricesUsdToCustomerRate && priceUsdLb) {
      return ((priceUsdLb * opportunityPricesUsdToCustomerRate) / fixedPrice).toFixed(6);
    } else {
      return 0;
    }
  };
  const [measurementRate, setMeasurementRate] = useState(getMeasurementRate());

  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) {
      return;
    }

    setModifiedExchangeRate(false);
  }, [opportunityStore.selectedCustomer]);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    if (!opportunityStore.selectedCustomer) return;

    const customer = opportunityStore.selectedCustomer;
    const customerUsesBaseCurrency = isBaseCurrency(customer.currency);
    const customerUsesBaseUnit = isBaseUnitOfMeasure(customer.unit_of_measure);
    setSameCurrency(customerUsesBaseCurrency);
    setSameUnitOfMeasure(customerUsesBaseUnit);

    if (!needsPriceConversion(customerUsesBaseCurrency, customerUsesBaseUnit)) return;

    if (modifiedExchangeRate) return;

    customFetch(
      Routes.rate_per_currency_unit_api_v1_exchange_rates_path(),
      {
        currency_from: BASE_CURRENCY,
        currency_to: customer.currency,
        measurement_unit_from: BASE_UNIT_OF_MEASURE,
        measurement_unit_to: customer.unit_of_measure,
      },
      'POST'
    ).then((response) => {
      if (response.success) {
        const invertedRate = 1 / response.exchange_rate;
        setOpportunityPricesUsdToCustomerRate(invertedRate);
        setMeasurementRate(response.measurement_rate);
      }
    });
  }, [opportunityStore.selectedCustomer, modifiedExchangeRate]);

  const handleLocalChange = (value) => {
    if (contractType === 'local') {
      setFixedPrice(value);
    }
  };

  useEffect(() => {
    if (!hasMounted.current) return;
    const rates = {
      measurementRate,
      customerRate: opportunityPricesUsdToCustomerRate,
    };

    if (
      contractType != 'local' &&
      needsPriceConversion(sameCurrency, sameUnitOfMeasure)
    ) {
      setFixedPrice(usdLbToCustomerPrice(priceUsdLb, rates).toFixed(4));
    }
    if (contractType === 'local') {
      setPriceUsdLb(customerPriceToUsdLb(fixedPrice, rates).toFixed(4));
    }
  }, [priceUsdLb, opportunityPricesUsdToCustomerRate, measurementRate, fixedPrice]);

  return (
    <FieldSetWithTitle title={I18n.translate('contracts.form.price')}>
      <Grid>
        <OpportunitySelectGroup
          field={contractTypeField}
          value={contractType}
          onChange={setContractType}
          required
          showAsterisk={false}
        />
        <OpportunitySelectGroup
          field={priceTypeField}
          overrideOptions={priceTypeOptions}
          value={priceType}
          onChange={setPriceType}
          required
          showAsterisk={false}
        />
        <OpportunitySelectGroup
          field={probabilityField}
          value={probabilityField?.value}
        />
        {contractType != 'local' && (
          <>
            {priceType == 'fixed' && (
              <>
                {(!sameCurrency || !sameUnitOfMeasure) && (
                  <OpportunityTextInput
                    field={priceUsdLbField}
                    type='number'
                    step='0.0001'
                    value={priceUsdLb}
                    onChange={setPriceUsdLb}
                    append='USD/lb'
                  />
                )}
                {!sameCurrency && (
                  <>
                    <OpportunityTextInput
                      field={opportunityPricesUsdToCustomerRateField}
                      type='number'
                      step='0.0001'
                      min='0.0001'
                      readOnly={!modifiedExchangeRate}
                      onChange={setOpportunityPricesUsdToCustomerRate}
                      value={opportunityPricesUsdToCustomerRate}
                      append={`${
                        opportunityStore.selectedCustomer?.currency || 'USD'
                      } to USD`}
                    />
                    <OpportunityCheckbox
                      field={modifiedExchangeRateField}
                      checked={modifiedExchangeRate}
                      onChange={setModifiedExchangeRate}
                    />
                  </>
                )}
                {priceType == 'fixed' && (
                  <>
                    <OpportunityTextInput
                      field={fixedPriceField}
                      value={fixedPrice}
                      onChange={setFixedPrice}
                      readOnly={!sameCurrency || !sameUnitOfMeasure}
                      type='number'
                      step='0.0001'
                      append={
                        opportunityStore.selectedCustomer?.price_unit_currency || ''
                      }
                    />
                    <input type='hidden' name='opportunity[differential]' value='' />
                  </>
                )}
              </>
            )}
          </>
        )}
        {contractType === 'local' && (
          <>
            {opportunityPricesUsdToCustomerRateField && !sameCurrency && (
              <div style={{ display: 'none' }}>
                <OpportunityTextInput
                  field={opportunityPricesUsdToCustomerRateField}
                  type='number'
                  step='0.0001'
                  min='0.0001'
                  readOnly={!modifiedExchangeRate}
                  onChange={setOpportunityPricesUsdToCustomerRate}
                  value={opportunityPricesUsdToCustomerRate}
                  append={`${
                    opportunityStore.selectedCustomer?.currency || 'USD'
                  } to USD`}
                />
              </div>
            )}
            <OpportunityTextInput
              field={fixedPriceField}
              value={fixedPrice}
              onChange={(value) => {
                handleLocalChange(value);
              }}
              type='number'
              step='0.0001'
              append={opportunityStore.selectedCustomer?.price_unit_currency || ''}
            />
            <input type='hidden' name='opportunity[differential]' value='' />
            {(!sameCurrency || !sameUnitOfMeasure) && (
              <OpportunityTextInput
                field={priceUsdLbField}
                readOnly={!sameCurrency || !sameUnitOfMeasure}
                type='number'
                step='0.0001'
                value={priceUsdLb}
                onChange={setPriceUsdLb}
                append='USD/lb'
              />
            )}
          </>
        )}

        {priceType == 'differential' && (
          <>
            <OpportunityTextInput
              field={differentialField}
              type='number'
              step='0.0001'
              append='USD/lb'
            />
            <input type='hidden' name='opportunity[fixed_price]' value='' />
          </>
        )}
        <OpportunitySelectGroup
          field={incotermField}
          value={incoterm}
          onChange={setIncoterm}
          required
          showAsterisk={false}
        />
        {contractType && contractType === 'fwd' && (
          <OpportunityCheckbox
            field={sampleNeededField}
            checked={sampleNeeded}
            onChange={setSampleNeeded}
          />
        )}
      </Grid>
    </FieldSetWithTitle>
  );
};

export default observer(OpportunityContract);
